import classNames from 'classnames'
import { useLottie } from 'lottie-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { GiftIcon } from '@ui/icons/Gift.icon'
import { LockIcon } from '@ui/icons/Lock.icon'

import { getCurrencySymbol } from '@utils/transaction.utils'

import { LoyaltyTierDto } from '@model/IntroducingBrokerDetailsDto'

import { useMixedText, wrapLTR } from '@hooks/useMixedText'

import ConfettiLottie from '@assets/lotties/Confetti/Confetti.json'

import { LoyaltyTierMapped } from '../../../Loyalty/Loyalty'

import styles from './LoyaltyCard.module.scss'

const bronzeSubTierIds = [101, 102, 103]
const bronzeMaxTierId = 1

interface Props {
  tier: LoyaltyTierMapped
  onSubmitReward: (tier?: LoyaltyTierDto | LoyaltyTierMapped | null) => void
}

export const LoyaltyCard: React.FC<Props> = (props) => {
  const { tier, onSubmitReward } = props

  const { t } = useTranslation()
  const { View: Confetti } = useLottie({
    animationData: ConfettiLottie,
    loop: true,
  })

  const isBronzeSubTier = bronzeSubTierIds.includes(tier.id)
  const isBronzeMaxTier = bronzeMaxTierId === tier.id
  const [name, subTier] = isBronzeSubTier && tier.label ? tier.label.split(' ') : []
  const { parseMixedText } = useMixedText()
  const isArabic = useArabicSessionLanguage()

  function formatRewardAmount(amount: number): string {
    if (amount >= 10000) {
      return parseMixedText(amount.toString()).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }
    return amount.toString()
  }

  const isActionButtonDisabled =
    tier.isRejected ||
    tier.isCreated ||
    tier.isRedeemed ||
    (!tier.isApproved && !tier.isRejected && !tier.isCreated && !tier.isRedeemed)

  const buttonText = useMemo(() => {
    if (tier.isRedeemed) {
      return t('Claimed')
    }

    return undefined
  }, [t, tier.isRedeemed])

  const buttonIcon = useMemo(() => {
    if (tier.isRedeemed) {
      return undefined
    }

    if (tier.isRejected || tier.isCreated || (!tier.isApproved && !tier.isRedeemed)) {
      return LockIcon
    }

    if (tier.isApproved) {
      return GiftIcon
    }

    return LockIcon
  }, [tier.isApproved, tier.isCreated, tier.isRedeemed, tier.isRejected])

  const tierPrizeText = isArabic
    ? wrapLTR(`${getCurrencySymbol('USD')} ${formatRewardAmount(tier.rewardAmount)}`)
    : `${getCurrencySymbol('USD')} ${formatRewardAmount(tier.rewardAmount)}`

  return (
    <article
      className={classNames(styles.loyaltyCard, {
        [styles.active]: tier.isApproved,
        [styles.completed]: tier.isRedeemed,
      })}
      {...(tier.label && { 'aria-label': tier.label })}
    >
      {tier.isApproved && <div className={styles.confetti}>{Confetti}</div>}
      <header
        className={classNames(styles.topSection, {
          [styles.noLabel]: !isBronzeSubTier && !isBronzeMaxTier,
        })}
      >
        {tier.imageUri && (
          <img
            src={tier.imageUri}
            alt={tier.label || 'loyalty tier icon'}
            width={48}
            height={48}
            loading='lazy'
          />
        )}

        <div className={styles.title}>
          <Body1>{isBronzeSubTier ? name : tier.label}</Body1>
          {isBronzeSubTier && <Body1>{`${t('IB.MultiTier.Sub Level')} ${subTier}`}</Body1>}
          {!isBronzeSubTier && isBronzeMaxTier && <Body1>{t('IB.MultiTier.Sub Level')} 4/4</Body1>}
        </div>

        <section className={styles.prize}>
          <GiftIcon />
          <Body1 medium>{tierPrizeText}</Body1>
        </section>
      </header>
      <Tooltip
        content={t('IB.Check eligibility criteria')}
        disabled={tier.isRedeemed || !isActionButtonDisabled}
        wrapperClassName={styles.tooltip}
      >
        <Button.Primary
          disabled={isActionButtonDisabled}
          size='md'
          fullWidth
          iconRight={buttonIcon}
          onClick={() => onSubmitReward(tier)}
          aria-disabled={isActionButtonDisabled}
          aria-label={
            tier.isApproved
              ? t('IB.Dashboard.Claim Rewards')
              : tier.isRedeemed
              ? t('Claimed')
              : t('Locked')
          }
        >
          {buttonText}
        </Button.Primary>
      </Tooltip>
    </article>
  )
}
